import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { orgsQuery } from "@/project/lib/queries";

export const Route = createFileRoute("/organizations")({
  head: () => ({
    meta: [
      { title: "Partner Organizations · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Maintain the charitable organizations that receive recommendation letters, with addresses and visiting days.",
      },
      { property: "og:title", content: "Partner Organizations" },
      {
        property: "og:description",
        content:
          "Maintain charitable organizations, addresses and visiting days.",
      },
    ],
  }),
  component: OrganizationsPage,
});

type OrgDraft = {
  id?: string;
  name: string;
  short_name: string;
  address_line1: string;
  address_line2: string;
  area: string;
  city: string;
  pincode: string;
  contact_person: string;
  contact_number: string;
  visiting_day: string;
  visiting_time: string;
  instructions: string;
  display_seq: number;
  active: boolean;
};

const blank: OrgDraft = {
  name: "",
  short_name: "",
  address_line1: "",
  address_line2: "",
  area: "",
  city: "Mumbai",
  pincode: "",
  contact_person: "",
  contact_number: "",
  visiting_day: "",
  visiting_time: "",
  instructions: "",
  display_seq: 99,
  active: true,
};

function OrganizationsPage() {
  const qc = useQueryClient();
  const { data: orgs } = useQuery(orgsQuery);
  const [draft, setDraft] = useState<OrgDraft | null>(null);

  const set = (k: keyof OrgDraft, v: string | number | boolean) =>
    setDraft(d => (d ? { ...d, [k]: v } : d));

  const save = async () => {
    if (!draft) {
      return;
    }
    if (!draft.name.trim()) {
      toast.error("Organization name is required");
      return;
    }
    const { id, ...payload } = draft;
    const { error } = id
      ? await supabase.from("organizations").update(payload).eq("id", id)
      : await supabase.from("organizations").insert(payload);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Organization saved");
    setDraft(null);
    qc.invalidateQueries({ queryKey: ["organizations"] });
  };

  const toggle = async (id: string, active: boolean) => {
    await supabase.from("organizations").update({ active }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["organizations"] });
  };

  return (
    <Shell>
      <PageHeader
        title="Partner Organizations"
        subtitle="Organizations that receive our recommendation letters"
        actions={
          <Button onClick={() => setDraft({ ...blank })}>
            <Plus className="mr-2 h-4 w-4" /> Add Organization
          </Button>
        }
      />

      {draft && (
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {draft.id ? "Edit Organization" : "New Organization"}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <Field label="Organization Name *">
              <Input
                value={draft.name}
                onChange={e => set("name", e.target.value)}
              />
            </Field>
            <Field label="Short Name">
              <Input
                value={draft.short_name}
                onChange={e => set("short_name", e.target.value)}
              />
            </Field>
            <Field label="Display Order">
              <Input
                type="number"
                value={draft.display_seq}
                onChange={e => set("display_seq", Number(e.target.value))}
              />
            </Field>
            <Field label="Address Line 1">
              <Input
                value={draft.address_line1}
                onChange={e => set("address_line1", e.target.value)}
              />
            </Field>
            <Field label="Address Line 2">
              <Input
                value={draft.address_line2}
                onChange={e => set("address_line2", e.target.value)}
              />
            </Field>
            <Field label="Area">
              <Input
                value={draft.area}
                onChange={e => set("area", e.target.value)}
              />
            </Field>
            <Field label="City">
              <Input
                value={draft.city}
                onChange={e => set("city", e.target.value)}
              />
            </Field>
            <Field label="Pincode">
              <Input
                value={draft.pincode}
                onChange={e => set("pincode", e.target.value)}
              />
            </Field>
            <Field label="Contact Person">
              <Input
                value={draft.contact_person}
                onChange={e => set("contact_person", e.target.value)}
              />
            </Field>
            <Field label="Contact Number">
              <Input
                value={draft.contact_number}
                onChange={e => set("contact_number", e.target.value)}
              />
            </Field>
            <Field label="Visiting Day">
              <Input
                value={draft.visiting_day}
                onChange={e => set("visiting_day", e.target.value)}
                placeholder="Every Sunday"
              />
            </Field>
            <Field label="Visiting Time">
              <Input
                value={draft.visiting_time}
                onChange={e => set("visiting_time", e.target.value)}
                placeholder="10:00 am to 12:00 pm"
              />
            </Field>
            <div className="md:col-span-3">
              <Field label="Instructions / Notes">
                <Textarea
                  value={draft.instructions}
                  onChange={e => set("instructions", e.target.value)}
                />
              </Field>
            </div>
            <div className="flex gap-3 md:col-span-3">
              <Button onClick={save}>
                <Save className="mr-2 h-4 w-4" /> Save
              </Button>
              <Button variant="outline" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {(orgs ?? []).map((o: any) => (
          <Card key={o.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{o.short_name || o.name}</p>
                  <p className="text-sm text-muted-foreground">{o.name}</p>
                  <p className="mt-1 text-sm">
                    {[
                      o.address_line1,
                      o.address_line2,
                      o.area,
                      o.city,
                      o.pincode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {o.visiting_day} {o.visiting_time}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Switch
                    checked={o.active}
                    onCheckedChange={c => toggle(o.id, c)}
                    aria-label="Active"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setDraft({
                        id: o.id,
                        name: o.name,
                        short_name: o.short_name ?? "",
                        address_line1: o.address_line1 ?? "",
                        address_line2: o.address_line2 ?? "",
                        area: o.area ?? "",
                        city: o.city ?? "",
                        pincode: o.pincode ?? "",
                        contact_person: o.contact_person ?? "",
                        contact_number: o.contact_number ?? "",
                        visiting_day: o.visiting_day ?? "",
                        visiting_time: o.visiting_time ?? "",
                        instructions: o.instructions ?? "",
                        display_seq: o.display_seq ?? 99,
                        active: o.active,
                      })
                    }
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </Shell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
